import React, { useState } from 'react';
import {
  ShoppingCart, Package, Truck, AlertTriangle, Clock, 
  TrendingUp, DollarSign, Calendar, Search, Filter, Plus,
  Eye, Edit, Trash2, CheckCircle, XCircle, Clock3
} from 'lucide-react';

interface PurchaseOrder {
  id: string;
  supplier: string;
  items: number;
  total: number;
  status: 'pending' | 'approved' | 'shipped' | 'delivered' | 'cancelled';
  orderDate: string;
  expectedDelivery: string;
  priority: 'low' | 'medium' | 'high';
}

interface OrderItem {
  id: string;
  medication: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  supplier: string;
  availability: 'in_stock' | 'low_stock' | 'out_of_stock';
}

const mockPurchaseOrders: PurchaseOrder[] = [
  {
    id: 'PO-001',
    supplier: 'MedSupply Co.',
    items: 15,
    total: 2847.50,
    status: 'pending',
    orderDate: '2024-01-15',
    expectedDelivery: '2024-01-22',
    priority: 'high'
  },
  {
    id: 'PO-002',
    supplier: 'PharmaDirect',
    items: 8,
    total: 1245.75,
    status: 'approved',
    orderDate: '2024-01-14',
    expectedDelivery: '2024-01-21',
    priority: 'medium'
  },
  {
    id: 'PO-003',
    supplier: 'Global Meds',
    items: 22,
    total: 3892.00,
    status: 'shipped',
    orderDate: '2024-01-12',
    expectedDelivery: '2024-01-19',
    priority: 'low'
  },
  {
    id: 'PO-004',
    supplier: 'QuickRx Supply',
    items: 12,
    total: 2156.25,
    status: 'delivered',
    orderDate: '2024-01-10',
    expectedDelivery: '2024-01-17',
    priority: 'medium'
  }
];

const mockOrderItems: OrderItem[] = [
  {
    id: 'OI-001',
    medication: 'Lisinopril 10mg',
    quantity: 500,
    unitPrice: 0.85,
    totalPrice: 425.00,
    supplier: 'MedSupply Co.',
    availability: 'in_stock'
  },
  {
    id: 'OI-002',
    medication: 'Metformin 500mg',
    quantity: 300,
    unitPrice: 0.45,
    totalPrice: 135.00,
    supplier: 'PharmaDirect',
    availability: 'low_stock'
  },
  {
    id: 'OI-003',
    medication: 'Atorvastatin 20mg',
    quantity: 400,
    unitPrice: 1.20,
    totalPrice: 480.00,
    supplier: 'Global Meds',
    availability: 'out_of_stock'
  }
];

const getStatusColor = (status: string) => {
  switch (status) {
    case 'pending': return 'border-yellow-500 bg-yellow-50 text-yellow-700';
    case 'approved': return 'border-blue-500 bg-blue-50 text-blue-700';
    case 'shipped': return 'border-purple-500 bg-purple-50 text-purple-700';
    case 'delivered': return 'border-green-500 bg-green-50 text-green-700';
    case 'cancelled': return 'border-red-500 bg-red-50 text-red-700';
    default: return 'border-gray-500 bg-gray-50 text-gray-700';
  }
};

const getPriorityColor = (priority: string) => {
  switch (priority) {
    case 'high': return 'bg-red-100 text-red-800 border-red-200';
    case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'low': return 'bg-green-100 text-green-800 border-green-200';
    default: return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

const getAvailabilityColor = (availability: string) => {
  switch (availability) {
    case 'in_stock': return 'bg-green-100 text-green-800 border-green-200';
    case 'low_stock': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'out_of_stock': return 'bg-red-100 text-red-800 border-red-200';
    default: return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

export function Ordering() {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');

  const filteredOrders = mockPurchaseOrders.filter(order => {
    const matchesSearch = order.supplier.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         order.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    const matchesPriority = priorityFilter === 'all' || order.priority === priorityFilter;
    
    return matchesSearch && matchesStatus && matchesPriority;
  });

  const orderStats = {
    total: mockPurchaseOrders.length,
    pending: mockPurchaseOrders.filter(o => o.status === 'pending').length,
    approved: mockPurchaseOrders.filter(o => o.status === 'approved').length,
    shipped: mockPurchaseOrders.filter(o => o.status === 'shipped').length,
    delivered: mockPurchaseOrders.filter(o => o.status === 'delivered').length,
    totalValue: mockPurchaseOrders.reduce((sum, o) => sum + o.total, 0)
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#4B0082] via-[#6B38FB] to-purple-600 rounded-3xl p-10 text-white shadow-2xl shadow-[#6B38FB]/25">
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-white/5 rounded-full blur-2xl animate-pulse delay-500"></div>
        </div>
        
        <div className="relative z-10">
          <div className="flex items-center space-x-4 mb-6">
            <div className="p-4 bg-white/20 backdrop-blur-sm rounded-2xl border border-white/30">
              <ShoppingCart className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-4xl font-black mb-2">Ordering System</h1>
              <p className="text-xl text-purple-100">Manage suppliers, purchase orders, and inventory procurement</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <p className="text-purple-200 text-sm">Total Orders</p>
              <p className="text-2xl font-black">{orderStats.total}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <p className="text-purple-200 text-sm">Pending</p>
              <p className="text-2xl font-black text-yellow-300">{orderStats.pending}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <p className="text-purple-200 text-sm">Total Value</p>
              <p className="text-2xl font-black">${orderStats.totalValue.toLocaleString()}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <p className="text-purple-200 text-sm">Suppliers</p>
              <p className="text-2xl font-black">12</p>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-xl">
        <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search orders, suppliers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-[#6B38FB]/20 focus:border-[#6B38FB] transition-all duration-300"
              />
            </div>
          </div>
          
          <div className="flex gap-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-[#6B38FB]/20 focus:border-[#6B38FB] transition-all duration-300"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="shipped">Shipped</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
            
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-[#6B38FB]/20 focus:border-[#6B38FB] transition-all duration-300"
            >
              <option value="all">All Priorities</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
          
          <button className="px-6 py-3 bg-gradient-to-r from-[#4B0082] to-[#6B38FB] text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-[#6B38FB]/25 transition-all duration-300 hover:scale-105 flex items-center space-x-2">
            <Plus className="w-5 h-5" />
            <span>New Order</span>
          </button>
        </div>
      </div>

      {/* Purchase Orders Table */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-[#4B0082]/5 to-[#6B38FB]/5">
          <h3 className="text-xl font-bold text-gray-900">Purchase Orders</h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Order ID</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Supplier</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Items</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Total</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Priority</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Expected Delivery</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredOrders.map((order) => (
                <tr key={order.id} className="hover:bg-gray-50 transition-colors duration-200">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-semibold text-gray-900">{order.id}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">{order.supplier}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">{order.items}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-semibold text-gray-900">${order.total.toLocaleString()}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full border ${getStatusColor(order.status)}`}>
                      {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full border ${getPriorityColor(order.priority)}`}>
                      {order.priority.charAt(0).toUpperCase() + order.priority.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">{order.expectedDelivery}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex space-x-2">
                      <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-blue-100 rounded-xl">
              <Package className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">Create Order</h4>
              <p className="text-sm text-gray-600">Start a new purchase order</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-green-100 rounded-xl">
              <Truck className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">Track Shipments</h4>
              <p className="text-sm text-gray-600">Monitor delivery status</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-purple-100 rounded-xl">
              <TrendingUp className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">Analytics</h4>
              <p className="text-sm text-gray-600">View ordering insights</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
